import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { formatLocation } from '../../../utils/formatters';
import { 
  HeartIcon, 
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ErrorMessage from '../../../components/common/ErrorMessage';

const Favorites = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTourId, setSelectedTourId] = useState(null);
  const queryClient = useQueryClient();
  
  // Fetch favorites
  const fetchFavorites = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.get('/api/v1/user/favorites', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching favorites:', error);
      throw error;
    }
  };
  
  // Fallback images for different categories
  const getFallbackImage = (category) => {
    const fallbacks = {
      'Wildlife Safari': 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Beach Vacation': 'https://images.unsplash.com/photo-1520454974749-611b7248ffdb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Mountain Climbing': 'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Adventure': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Cultural Experience': 'https://images.unsplash.com/photo-1489493585363-d69421e0edd3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'City Tour': 'https://images.unsplash.com/photo-1611348524140-53c9a25ad10a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'default': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80'
    };
    
    if (!category) return fallbacks.default;
    
    // If we have categories as an array, use the first one
    const categoryName = Array.isArray(category) && category.length > 0 
      ? category[0].name 
      : (typeof category === 'string' ? category : 'default');
      
    return fallbacks[categoryName] || fallbacks.default;
  };
  
  // Get image URL
  const getImageUrl = (tour) => {
    const apiBaseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
    
    // If image is a full URL
    if (tour.image && (tour.image.startsWith('http://') || tour.image.startsWith('https://'))) {
      return tour.image;
    }
    
    // If featured_image is available
    if (tour.featured_image) {
      // If image is a full URL
      if (tour.featured_image.startsWith('http://') || tour.featured_image.startsWith('https://')) {
        return tour.featured_image;
      }
      
      // If image is a relative path - properly handle storage paths
      return `${apiBaseUrl}/storage/${tour.featured_image}`;
    }
    
    // If image is a relative path
    if (tour.image) {
      return `${apiBaseUrl}/storage/${tour.image}`;
    }
    
    return getFallbackImage(tour.categories || tour.category);
  };

  // Favorites query
  const { 
    data, 
    isLoading, 
    error,
    refetch 
  } = useQuery('userFavorites', fetchFavorites, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Remove from favorites mutation
  const removeFavoriteMutation = useMutation(
    async (tourId) => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.delete(`/api/v1/user/favorites/${tourId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userFavorites');
        setShowConfirmModal(false);
        setSelectedTourId(null);
      }
    }
  );
  
  const handleRemoveFavorite = (tourId) => {
    setSelectedTourId(tourId);
    setShowConfirmModal(true);
  };
  
  const confirmRemoveFavorite = () => {
    if (selectedTourId) {
      removeFavoriteMutation.mutate(selectedTourId);
    }
  };
  
  const cancelRemoveFavorite = () => {
    setShowConfirmModal(false);
    setSelectedTourId(null);
  };
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <ErrorMessage 
          message="Failed to load your favorites. Please try again later." 
          onRetry={refetch} 
        />
      </div>
    );
  }

  // Extract favorites from the nested data structure
  const favorites = data?.data?.data || [];
  
  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <HeartSolidIcon className="h-5 w-5 text-red-500 mr-2" />
            <h2 className="text-xl font-bold text-gray-800">My Favorites</h2>
          </div>
          <p className="text-gray-600">Tours you've saved for future reference</p>
        </div>
        
        <div className="p-6">
          {favorites.length === 0 ? (
            <div className="text-center py-12">
              <HeartIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Favorites Yet</h3>
              <p className="text-gray-500 mb-6">You haven't added any tours to your favorites yet.</p>
              <Link
                to="/tours"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
              >
                Explore Tours
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favorites.map((tour) => (
                <div key={tour.id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative">
                    <img
                      src={getImageUrl(tour)}
                      alt={tour.title || tour.name}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = getFallbackImage(tour.categories || tour.category);
                      }}
                    />
                    <button
                      onClick={() => handleRemoveFavorite(tour.id)}
                      className="absolute top-3 right-3 p-1.5 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                    >
                      <HeartSolidIcon className="h-5 w-5 text-red-500" />
                    </button>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-bold text-gray-800 mb-1">
                      <Link to={`/tours/${tour.id}`} className="hover:text-primary">
                        {tour.title}
                      </Link>
                    </h3>
                    <div className="flex items-center text-gray-500 text-sm mb-3">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      <span>{formatLocation(tour.location)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                        <span className="text-sm font-medium">
                          {tour.rating} ({tour.review_count || 0} reviews)
                        </span>
                      </div>
                      <div className="text-primary font-bold">
                        ${tour.price}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        <span>{tour.duration} days</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <UserGroupIcon className="h-4 w-4 mr-1" />
                        <span>Max {tour.group_size}</span>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-100 pt-3 flex justify-between items-center">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Next available date:</p>
                        <p className="text-sm font-medium">
                          {tour.start_dates && tour.start_dates.length > 0 
                            ? new Date(tour.start_dates[0]).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'Not available'}
                        </p>
                      </div>
                      <Link
                        to={`/tours/${tour.id}`}
                        className="inline-flex items-center px-3 py-1.5 border border-primary text-sm font-medium rounded-md text-primary hover:bg-primary-50"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Confirm Remove Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <HeartIcon className="h-10 w-10 text-red-500" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-center mb-2">Remove from Favorites</h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to remove this tour from your favorites?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelRemoveFavorite}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveFavorite}
                disabled={removeFavoriteMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {removeFavoriteMutation.isLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Favorites;
